/**
 * Work Log Review page — task 159c (read-only) + task 159d (Classify modal).
 *
 * Admin/owner-only page at /reports/work-log-review. Renders the four-region
 * layout from parent task 159 locked decision #1: filter strip + group-by
 * pill bar above a top-N ECharts bar, sortable group-stats table, and a
 * paginated raw-event log. The Classify modal lands per locked decision #8;
 * non-admin users are bounced to the dashboard.
 *
 * URL state (everything except `dateRange`, which lives on AppContext per
 * DESIGN-RULES Rule 35):
 *   classificationSource[]  default ['fallback']
 *   employeeIds[]           default []
 *   businessUnitId          default ''
 *   clientId, projectId     default ''
 *   classifiedByRuleId      default ''
 *   groupBy[]               default []
 *   sortBy                  default 'hours'
 *   sortDir                 default 'desc'
 *   page, pageSize          default 1, 100
 *   __capHit                fixture-mode-only knob to render the cap-hit hint
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { PageHeader } from '@/components/composites/PageHeader';
import { WhyPanel } from '@/components/composites/WhyPanel';
import { useSuggestion } from '@/hooks/useSuggestion';
import {
  WorkLogReviewFilters,
  type WorkLogReviewFiltersValue,
} from '@/components/composites/WorkLogReviewFilters';
import { WorkLogReviewGroupByPills } from '@/components/composites/WorkLogReviewGroupByPills';
import { WorkLogReviewStatsTable } from '@/components/composites/WorkLogReviewStatsTable';
import { WorkLogReviewTopNChart } from '@/components/composites/WorkLogReviewTopNChart';
import { WorkLogReviewEventLog } from '@/components/composites/WorkLogReviewEventLog';
import { useAppContext } from '@/context/AppContext';
import { useWorkLogReview } from '@/hooks/useWorkLogReview';
import { WORK_LOG_REVIEW_GROUP_CAP_DEFAULT } from '@/data/workLogReview';
import {
  GROUP_BY_DIMENSIONS,
  type ClassificationSource,
  type GroupByDimension,
  type SortByColumn,
  type WorkLogReviewEventRow,
  type WorkLogReviewGroupRow,
} from '@/types/workLogReview';
import { ClassifyModal, type ClassifyTarget } from '@/pages/workLogReview/ClassifyModal';
import type { ClassifyScopeFilter, ClassifyWorkLogResult } from '@/hooks/useClassifyWorkLog';
import { matchesGroupKey } from '@/pages/workLogReview/matchesGroupKey';
import styles from './WorkLogReviewPage.module.css';

const ALL_SOURCES: readonly ClassificationSource[] = [
  'fallback',
  'rule',
  'manual',
  'ai_text',
  'ai_vision',
  'ai',
];
const ALL_SORT_BYS: readonly SortByColumn[] = [
  'count',
  'hours',
  'distinctEmployees',
  'firstSeenAt',
  'lastSeenAt',
  'percentOfFiltered',
  // Task 204 D9 — admin-only sort key. Backend ranks by the row's
  // aggregate fused confidence score; descending = high-confidence
  // first.
  'confidence',
];

interface PageState {
  classificationSource: ClassificationSource[];
  employeeIds: string[];
  businessUnitId: string;
  clientId: string;
  projectId: string;
  classifiedByRuleId: string;
  groupBy: GroupByDimension[];
  sortBy: SortByColumn;
  sortDir: 'asc' | 'desc';
  page: number;
  pageSize: number;
  capHitMode: boolean;
}

function parseClassificationSources(raw: string[]): ClassificationSource[] {
  const out: ClassificationSource[] = [];
  for (const v of raw) {
    if ((ALL_SOURCES as readonly string[]).includes(v)) out.push(v as ClassificationSource);
  }
  return out;
}

function parseGroupBy(raw: string[]): GroupByDimension[] {
  const out: GroupByDimension[] = [];
  for (const v of raw) {
    if ((GROUP_BY_DIMENSIONS as readonly string[]).includes(v)) out.push(v as GroupByDimension);
  }
  return out;
}

function parseSortBy(raw: string | null): SortByColumn {
  if (raw && (ALL_SORT_BYS as readonly string[]).includes(raw)) return raw as SortByColumn;
  return 'hours';
}

function parseSortDir(raw: string | null): 'asc' | 'desc' {
  return raw === 'asc' ? 'asc' : 'desc';
}

function parsePositiveInt(raw: string | null, fallback: number, max: number): number {
  if (!raw) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return fallback;
  return Math.min(n, max);
}

function readState(search: URLSearchParams): PageState {
  const sources = parseClassificationSources(search.getAll('classificationSource'));
  return {
    classificationSource: sources.length > 0 ? sources : ['fallback'],
    employeeIds: search.getAll('employeeIds'),
    businessUnitId: search.get('businessUnitId') ?? '',
    clientId: search.get('clientId') ?? '',
    projectId: search.get('projectId') ?? '',
    classifiedByRuleId: search.get('classifiedByRuleId') ?? '',
    groupBy: parseGroupBy(search.getAll('groupBy')),
    sortBy: parseSortBy(search.get('sortBy')),
    sortDir: parseSortDir(search.get('sortDir')),
    page: parsePositiveInt(search.get('page'), 1, 1_000_000),
    pageSize: parsePositiveInt(search.get('pageSize'), 100, 200),
    capHitMode: search.get('__capHit') === '1',
  };
}

function writeState(state: PageState): URLSearchParams {
  const next = new URLSearchParams();
  for (const s of state.classificationSource) next.append('classificationSource', s);
  for (const id of state.employeeIds) next.append('employeeIds', id);
  if (state.businessUnitId) next.set('businessUnitId', state.businessUnitId);
  if (state.clientId) next.set('clientId', state.clientId);
  if (state.projectId) next.set('projectId', state.projectId);
  if (state.classifiedByRuleId) next.set('classifiedByRuleId', state.classifiedByRuleId);
  for (const dim of state.groupBy) next.append('groupBy', dim);
  if (state.sortBy !== 'hours') next.set('sortBy', state.sortBy);
  if (state.sortDir !== 'desc') next.set('sortDir', state.sortDir);
  if (state.page !== 1) next.set('page', String(state.page));
  if (state.pageSize !== 100) next.set('pageSize', String(state.pageSize));
  if (state.capHitMode) next.set('__capHit', '1');
  return next;
}

interface PageFeedback {
  tone: 'success' | 'danger';
  text: string;
}

function buildSuccessMessage(
  result: ClassifyWorkLogResult,
  context: {
    project: { name: string; clientName: string };
    ruleSubmitted: boolean;
  },
): string {
  const n = result.appliedManualOverrides;
  const eventWord = n === 1 ? 'event' : 'events';
  const dest = `${context.project.clientName} / ${context.project.name}`;
  if (context.ruleSubmitted && result.createdRule) {
    return `Classified ${n} ${eventWord} to ${dest}. Rule "${result.createdRule.name}" was created and will start matching new activity automatically.`;
  }
  return `Classified ${n} ${eventWord} to ${dest}. No new rule was created.`;
}

export function WorkLogReviewPage() {
  const { currentUser } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => readState(searchParams), [searchParams]);

  const updateState = useCallback(
    (patch: Partial<PageState>) => {
      const next = { ...state, ...patch };
      // Any change to filters / group-by / sort resets pagination — the
      // current page number is meaningless against a different result set.
      const resetsPage =
        patch.classificationSource !== undefined ||
        patch.employeeIds !== undefined ||
        patch.businessUnitId !== undefined ||
        patch.clientId !== undefined ||
        patch.projectId !== undefined ||
        patch.classifiedByRuleId !== undefined ||
        patch.groupBy !== undefined ||
        patch.sortBy !== undefined ||
        patch.sortDir !== undefined ||
        patch.pageSize !== undefined;
      if (resetsPage && patch.page === undefined) next.page = 1;
      setSearchParams(writeState(next));
    },
    [state, setSearchParams],
  );

  const onSort = (sortBy: SortByColumn) => {
    if (state.sortBy === sortBy) {
      updateState({ sortDir: state.sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updateState({ sortBy, sortDir: 'desc' });
    }
  };

  const filtersValue: WorkLogReviewFiltersValue = {
    classificationSource: state.classificationSource,
    employeeIds: state.employeeIds,
    businessUnitId: state.businessUnitId,
    clientId: state.clientId,
    projectId: state.projectId,
  };

  const onFiltersChange = (next: WorkLogReviewFiltersValue) => {
    updateState({
      classificationSource:
        next.classificationSource.length === 0 ? ['fallback'] : [...next.classificationSource],
      employeeIds: [...next.employeeIds],
      businessUnitId: next.businessUnitId,
      clientId: next.clientId,
      projectId: next.projectId,
    });
  };

  const { data, isLoading } = useWorkLogReview({
    classificationSource: state.classificationSource,
    employeeIds: state.employeeIds.length > 0 ? state.employeeIds : undefined,
    businessUnitId: state.businessUnitId || undefined,
    clientId: state.clientId || undefined,
    projectId: state.projectId || undefined,
    classifiedByRuleId: state.classifiedByRuleId || undefined,
    groupBy: state.groupBy,
    sortBy: state.sortBy,
    sortDir: state.sortDir,
    page: state.page,
    pageSize: state.pageSize,
    capHitMode: state.capHitMode,
  });

  // ── Modal + multi-select state (task 159d) ────────────────────────────────
  const [classifyTarget, setClassifyTarget] = useState<ClassifyTarget | null>(null);
  // The selection is keyed by a "selection epoch" string derived from the
  // filter / group-by / pagination. When the user changes any of those,
  // the epoch changes and the stored set is logically empty (the lookup
  // helper returns empty until a new selection lands under the new epoch).
  // This keeps the side-effect of clearing selection out of an effect.
  const selectionEpoch = useMemo(
    () =>
      JSON.stringify([
        state.classificationSource,
        state.employeeIds,
        state.businessUnitId,
        state.clientId,
        state.projectId,
        state.classifiedByRuleId,
        state.groupBy,
        state.page,
        state.pageSize,
      ]),
    [state],
  );
  const [selection, setSelection] = useState<{ epoch: string; ids: ReadonlySet<string> }>({
    epoch: selectionEpoch,
    ids: new Set(),
  });
  const selectedEventIds = useMemo<ReadonlySet<string>>(
    () => (selection.epoch === selectionEpoch ? selection.ids : new Set<string>()),
    [selection, selectionEpoch],
  );

  const [feedback, setFeedback] = useState<PageFeedback | null>(null);

  // Task 204 D9 — Why? panel state. The operator surface opens the panel
  // with the Diagnostics expander available. Suggestion is fetched on
  // demand via useSuggestion.
  const [whyEventId, setWhyEventId] = useState<string | null>(null);
  const { data: whySuggestion } = useSuggestion(whyEventId);

  // Auto-dismiss the feedback banner after 6s — mirrors ManageRulesPage's
  // pattern. Re-runs whenever a new feedback banner appears.
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 6000);
    return () => clearTimeout(t);
  }, [feedback]);

  const events = useMemo<readonly WorkLogReviewEventRow[]>(() => data?.events?.rows ?? [], [data]);

  const currentFilter: ClassifyScopeFilter = useMemo(
    () => ({
      classificationSource: state.classificationSource,
      employeeIds: state.employeeIds.length > 0 ? state.employeeIds : undefined,
      businessUnitId: state.businessUnitId || undefined,
      clientId: state.clientId || undefined,
      projectId: state.projectId || undefined,
      classifiedByRuleId: state.classifiedByRuleId || undefined,
      // `from` / `to` are pulled from `dateRange` server-side — see
      // useWorkLogReview. The classify endpoint defaults the same way
      // (server-side last-7-days when from/to are omitted), so the modal
      // does not need to forward them explicitly.
    }),
    [state],
  );

  const onClassifyGroup = useCallback(
    (row: WorkLogReviewGroupRow) => {
      const eventsInScope = events.filter((e) => matchesGroupKey(e, row.key));
      setClassifyTarget({
        kind: 'group',
        row,
        filter: currentFilter,
        eventsInScope,
      });
    },
    [events, currentFilter],
  );

  const onClassifyEvent = useCallback(
    (eventId: string) => {
      const target = events.find((e) => e.id === eventId);
      if (!target) return;
      setClassifyTarget({ kind: 'events', events: [target] });
    },
    [events],
  );

  const onClassifyBulk = useCallback(() => {
    const selected = events.filter((e) => selectedEventIds.has(e.id));
    if (selected.length === 0) return;
    setClassifyTarget({ kind: 'events', events: selected });
  }, [events, selectedEventIds]);

  const onToggleSelect = useCallback(
    (eventId: string) => {
      setSelection((prev) => {
        const base = prev.epoch === selectionEpoch ? prev.ids : new Set<string>();
        const next = new Set(base);
        if (next.has(eventId)) next.delete(eventId);
        else next.add(eventId);
        return { epoch: selectionEpoch, ids: next };
      });
    },
    [selectionEpoch],
  );

  const onToggleSelectAll = useCallback(
    (selectAll: boolean, eventIds: readonly string[]) => {
      setSelection((prev) => {
        const base = prev.epoch === selectionEpoch ? prev.ids : new Set<string>();
        const next = new Set(base);
        if (selectAll) {
          for (const id of eventIds) next.add(id);
        } else {
          for (const id of eventIds) next.delete(id);
        }
        return { epoch: selectionEpoch, ids: next };
      });
    },
    [selectionEpoch],
  );

  const onClassifySuccess = useCallback(
    (
      result: ClassifyWorkLogResult,
      context: {
        project: { name: string; clientName: string };
        ruleSubmitted: boolean;
      },
    ) => {
      setFeedback({ tone: 'success', text: buildSuccessMessage(result, context) });
      setSelection({ epoch: selectionEpoch, ids: new Set() });
    },
    [selectionEpoch],
  );

  if (currentUser.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const groups = data?.groups ?? [];
  const totalCount = data?.events?.totalCount ?? 0;
  const groupCapHit = data?.groupCapHit ?? false;
  const groupCap = data?.groupCap ?? WORK_LOG_REVIEW_GROUP_CAP_DEFAULT;
  const selectedCount = selectedEventIds.size;

  return (
    <>
      <PageHeader
        title="Work Log Review"
        subtitle="Find activity that fell into a default category and reclassify it."
      />

      {feedback && (
        <InfoBanner tone={feedback.tone} dismissible onDismiss={() => setFeedback(null)}>
          {feedback.text}
        </InfoBanner>
      )}

      <div className={styles.controls}>
        <WorkLogReviewFilters value={filtersValue} onChange={onFiltersChange} />
        <WorkLogReviewGroupByPills
          value={state.groupBy}
          onChange={(next) => updateState({ groupBy: next })}
        />

        <div className={styles.actionsRow} data-testid="wlr-bulk-actions">
          <Button
            variant="primary"
            size="sm"
            disabled={selectedCount === 0}
            onClick={onClassifyBulk}
            data-testid="wlr-classify-bulk"
          >
            {selectedCount === 0 ? 'Classify selected' : `Classify selected (${selectedCount})`}
          </Button>
          {isLoading && <span className={styles.loading}>Loading…</span>}
        </div>
      </div>

      {groupCapHit && (
        <InfoBanner tone="warning" title="Showing the loudest groups only">
          The current view exceeds the {groupCap}-row cap. Narrow the filters to see the full
          breakdown.
        </InfoBanner>
      )}

      <WorkLogReviewTopNChart rows={groups} groupBy={state.groupBy} sortBy={state.sortBy} />

      <WorkLogReviewStatsTable
        rows={groups}
        groupBy={state.groupBy}
        sortBy={state.sortBy}
        sortDir={state.sortDir}
        onSort={onSort}
        onClassifyGroup={onClassifyGroup}
      />

      <WorkLogReviewEventLog
        events={events}
        groupBy={state.groupBy}
        page={state.page}
        pageSize={state.pageSize}
        totalCount={totalCount}
        onPageChange={(page) => updateState({ page })}
        onPageSizeChange={(pageSize) => updateState({ pageSize })}
        selectedEventIds={selectedEventIds}
        onToggleSelect={onToggleSelect}
        onToggleSelectAll={onToggleSelectAll}
        onClassifyEvent={onClassifyEvent}
        onOpenWhy={setWhyEventId}
      />

      <ClassifyModal
        open={classifyTarget !== null}
        target={classifyTarget}
        groupBy={state.groupBy}
        onClose={() => setClassifyTarget(null)}
        onSuccess={onClassifySuccess}
      />

      <WhyPanel
        open={whyEventId !== null}
        onClose={() => setWhyEventId(null)}
        suggestion={whySuggestion}
        diagnosticsEnabled
        context={
          whySuggestion
            ? {
                occurredAt:
                  events.find((e) => e.id === whyEventId)?.occurredAt ??
                  events[0]?.occurredAt ??
                  '1970-01-01T00:00:00Z',
                employeeName: events.find((e) => e.id === whyEventId)?.employeeName ?? null,
                tz: 'UTC',
              }
            : undefined
        }
      />
    </>
  );
}
